import mapKeys from 'lodash-es/mapKeys';
import { create } from 'zustand';

import { SECURE_API } from 'src/api/api';
import { MenuOrderUpdate } from 'src/helpers/publicPageMenuTree';
import { normalizeMenuLocations } from 'src/helpers/publicPageMenuLocations';
import {
  normalizePath,
  normalizePublicPageRefId,
  resolvePageByPath,
} from 'src/helpers/publicPageUtils';
import { IBasePublicPage, IPageRedirect, IPublicPage } from 'src/interfaces/PublicPage';
import { createDedupedFetcher } from 'src/stores/lib/dedupePromise';
import { runApiAction } from 'src/stores/lib/runApiAction';

function normalizePublicPagesFromApi(pages: IPublicPage[]): IPublicPage[] {
  return pages
    .map(page => {
      const id = page.id ?? (page as IPublicPage & { _id?: string })._id;
      if (!id) return null;
      return {
        ...page,
        id: String(id),
        redirectTo: normalizePublicPageRefId(page.redirectTo),
        menu: {
          ...page.menu,
          menuLocations: normalizeMenuLocations(page.menu),
          parentId: normalizePublicPageRefId(page.menu.parentId) ?? null,
        },
      };
    })
    .filter(page => page != null) as IPublicPage[];
}

function applyPublicPages(pages: IPublicPage[]) {
  const normalized = normalizePublicPagesFromApi(pages);
  return {
    publicPages: normalized,
    publicPagesById: mapKeys(normalized, 'id'),
    publicPagesByKey: mapKeys(normalized, 'key'),
  };
}

interface PublicPagesState {
  publicPages: IPublicPage[];
  publicPagesById?: Record<string, IPublicPage>;
  publicPagesByKey?: Record<string, IPublicPage>;
  redirects: IPageRedirect[];
  isLoading: boolean;
  /** True after the first publicPages fetch settles (success or error). */
  hasFetched: boolean;
  error: unknown | null;

  fetchPublicPages: () => Promise<void>;
  fetchRedirects: () => Promise<void>;
  savePublicPage: (page: IBasePublicPage, id?: string) => Promise<IPublicPage>;
  reorderMenu: (items: MenuOrderUpdate[]) => Promise<void>;
  deletePublicPage: (id: string) => Promise<void>;
  resolveByPath: (pathname: string, includeUnpublished?: boolean) => IPublicPage | undefined;
  reset: () => void;
}

const usePublicPagesStore = create<PublicPagesState>((set, get) => {
  const fetchPublicPagesImpl = async () => {
    await runApiAction({
      set,
      setLoading: true,
      clearLoadingOnFinish: true,
      action: () => SECURE_API.get<IPublicPage[]>('publicPages/'),
      onSuccess: ({ data }) => {
        set({ ...applyPublicPages(data), error: null, hasFetched: true });
      },
      onError: error => {
        set({ error, hasFetched: true });
      },
    });
  };

  const fetchRedirectsImpl = async () => {
    await runApiAction({
      set,
      action: () => SECURE_API.get<IPageRedirect[]>('publicPages/redirects'),
      onSuccess: ({ data }) => {
        set({ redirects: data, error: null });
      },
      onError: () => {
        set({ redirects: [] });
      },
    });
  };

  const fetchPublicPages = createDedupedFetcher(fetchPublicPagesImpl);
  const fetchRedirects = createDedupedFetcher(fetchRedirectsImpl);

  return {
  publicPages: [],
  publicPagesById: undefined,
  publicPagesByKey: undefined,
  redirects: [],
  isLoading: false,
  hasFetched: false,
  error: null,

  fetchPublicPages,

  fetchRedirects,

  savePublicPage: async (page, id) => {
    set({ isLoading: true, error: null });
    const result = await runApiAction({
      set,
      action: () =>
        id
          ? SECURE_API.put<IPublicPage>(`publicPages/${id}`, page)
          : SECURE_API.post<IPublicPage>('publicPages/', page),
      onError: error => {
        set({ isLoading: false, error });
      },
    });

    if (!result) {
      throw get().error ?? new Error('Failed to save public page');
    }

    await get().fetchPublicPages();
    set({ isLoading: false, error: null });
    return result.data;
  },

  reorderMenu: async items => {
    set({ isLoading: true, error: null });
    const result = await runApiAction({
      set,
      action: () => SECURE_API.put<IPublicPage[]>('publicPages/reorder/menu', { items }),
      onSuccess: ({ data }) => {
        set({ ...applyPublicPages(data), isLoading: false, error: null });
      },
      onError: error => {
        set({ isLoading: false, error });
      },
    });

    if (!result) {
      throw get().error ?? new Error('Failed to reorder menu');
    }
  },

  deletePublicPage: async id => {
    set({ isLoading: true, error: null });
    const deleted = await runApiAction({
      set,
      action: () => SECURE_API.delete(`publicPages/${id}`),
      onError: error => {
        set({ isLoading: false, error });
      },
    });

    if (!deleted) {
      return;
    }

    await get().fetchPublicPages();
    set({ isLoading: false, error: null });
  },

  resolveByPath: (pathname, includeUnpublished) =>
    resolvePageByPath(pathname, get().publicPages, { includeUnpublished }),

  reset: () =>
    set({
      publicPages: [],
      publicPagesById: undefined,
      publicPagesByKey: undefined,
      redirects: [],
      isLoading: false,
      hasFetched: false,
      error: null,
    }),
  };
});

export function findRedirectTarget(
  pathname: string,
  redirects: IPageRedirect[],
): string | undefined {
  const normalized = normalizePath(pathname);
  const match = redirects.find(r => normalizePath(r.fromPath) === normalized);
  return match ? normalizePath(match.toPath) : undefined;
}

export default usePublicPagesStore;
